import { HttpTransport } from '@estee/elc-http-transport';
import { root } from '@estee/elc-universal-utils';

import { ConfigRepository } from '~repositories/ConfigRepository';
import { ILoyaltyApiResponse } from '~domain/entities/LoyaltyInfo';

type IPgApiConfigKeys = 'apiAuthToken' | 'brandId' | 'regionId';
type IPgApiConfigs = Pick<ConfigRepository['config'], IPgApiConfigKeys>;

interface IGlobalHeaders {
    Authorization: string;
    AuthorizationCredentials: string;
}

interface IUserResponseId {
    user_id: string;
}

const BASE_API_PATH = '/rest/api/v1/ra';

export class LoyaltyApiSdk {
    private httpTransport: HttpTransport;
    private apiAuthToken: ConfigRepository['config']['apiAuthToken'];
    private brand: ConfigRepository['config']['brandId'];
    private region: ConfigRepository['config']['regionId'];

    constructor(httpTransport: HttpTransport) {
        this.httpTransport = httpTransport;
    }

    public setConfigs({ apiAuthToken, brandId, regionId }: IPgApiConfigs) {
        this.brand = brandId;
        this.region = regionId;
        this.apiAuthToken = apiAuthToken;

        this.httpTransport.addGlobalHeaders(this.getGlobalHeaders());
    }

    private getAuthToken = (): string => this.apiAuthToken;

    private getGlobalHeaders = (): IGlobalHeaders => {
        const auth = btoa(this.getAuthToken());
        const AuthorizationCredentials = `Basic ${auth}`;
        const Authorization = AuthorizationCredentials;

        return {
            Authorization,
            AuthorizationCredentials
        };
    };

    // @todo: Extract this into an elc-perlgem-api package
    private getBaseUrl = (): string => `${root.location.protocol}//${root.location.hostname}`;

    private getDefaultParams = () => ({ brand: this.brand, region: this.region });

    private userIdFallback = async (): Promise<IUserResponseId['user_id']> => {
        const params = this.getDefaultParams();
        const url = `${this.getBaseUrl()}/rest/api/v1/ra/user`;

        try {
            const userResponse = await this.httpTransport.get({
                url,
                params,
                options: {
                    credentials: 'include'
                }
            });

            return userResponse.data.user_id;
        } catch (err) {
            throw err;
        }
    };

    public getLoyaltyByUserId = async (userId: string): Promise<ILoyaltyApiResponse> => {
        const params = this.getDefaultParams();

        try {
            const id = userId ? userId : await this.userIdFallback();
            const url = `${this.getBaseUrl()}${BASE_API_PATH}/user/loyalty/${id}`;

            const loyaltyResponse = await this.httpTransport.get<ILoyaltyApiResponse>({
                url,
                params,
                options: {
                    credentials: 'include'
                }
            });

            return loyaltyResponse.data;
        } catch (err) {
            return err;
        }
    };
}
